import { Tags } from '../enums/tags.enum';
import { Allergens } from '../enums/allergens.enum';

export class IconsUtils {


/**
 * Get the Font Awesome icon class for a specific food type tag
 * @param tag The food type tag
 * @returns The corresponding Font Awesome icon class
 */
static getFoodTypeIcon(tag: Tags): string {
    switch (tag) {
        // Spice levels
        case Tags.SPICY:
          return 'fa-solid fa-fire-flame-curved';  // Bigger flame for very spicy

        // Dietary preferences
        case Tags.VEGAN:
            return 'fa-solid fa-leaf';          // Leaf icon for vegan
        case Tags.VEGETARIAN:
            return 'fa-solid fa-seedling';      // Seedling icon for vegetarian
        case Tags.GLUTEN_FREE:
            return 'fa-solid fa-wheat-awn-circle-exclamation';    // Crossed wheat for gluten-free
        case Tags.DAIRY_FREE:
            return 'fa-solid fa-bottle-water';   // Bottle for dairy-free
        case Tags.HALAL:
            return 'fa-solid fa-moon';          // Moon icon for halal
        case Tags.KOSHER:
            return 'fa-solid fa-star-of-david'; // Star of David for kosher
        case Tags.KETO:
            return 'fa-solid fa-drumstick-bite';  // Drumstick for keto
        case Tags.PALEO:
            return 'fa-solid fa-bone';          // Bone for paleo
        case Tags.SEAFOOD:
            return 'fa-solid fa-fish';          // Fish icon for seafood

        // Special features
        case Tags.NEW:
            return 'fa-solid fa-star';          // Star for new items
        case Tags.CHEFS_CHOICE:
            return 'fa-solid fa-utensils';      // Utensils for chef's choice
        case Tags.RECOMMENDED:
            return 'fa-solid fa-thumbs-up';     // Thumbs up for recommended
        case Tags.HOUSE_SPECIALTY:
            return 'fa-solid fa-award';         // Award for house specialty
        case Tags.LIMITED_EDITION:
            return 'fa-solid fa-clock';         // Clock for limited time
        case Tags.SHARING:
            return 'fa-solid fa-users';         // Users for sharing
        case Tags.BREAKFAST:
            return 'fa-solid fa-mug-hot';       // Hot mug for breakfast

        // Quality indicators
        case Tags.ORGANIC:
            return 'fa-solid fa-seedling';      // Seedling icon for organic
        case Tags.LOCAL:
            return 'fa-solid fa-house';         // House icon for local
        case Tags.HOMEMADE:
            return 'fa-solid fa-mortar-pestle'; // Mortar and pestle for homemade
        case Tags.NO_ADDITIVES:
            return 'fa-solid fa-ban';           // Ban icon for no additives

        default:
            return 'fa-solid fa-utensils';      // Default food icon
    }
}
  
/**
 * Get the Font Awesome icon class for a specific allergen
 * @param allergen The allergen type
 * @returns The corresponding Font Awesome icon class
 */
static getAllergenIcon(allergen: Allergens): string {
  switch (allergen) {
    case Allergens.GLUTEN:
      return 'fa-solid fa-wheat-awn';     // Wheat icon for gluten
    case Allergens.CRUSTACEANS:
      return 'fa-solid fa-shrimp';        // Shrimp icon for crustaceans
    case Allergens.EGGS:
      return 'fa-solid fa-egg';           // Egg icon
    case Allergens.FISH:
      return 'fa-solid fa-fish';          // Fish icon
    case Allergens.PEANUTS:
      return 'fa-solid fa-seedling';      // Seedling icon for peanuts
    case Allergens.SOYBEANS:
      return 'fa-solid fa-leaf';          // Leaf icon for soy
    case Allergens.MILK:
      return 'fa-solid fa-bottle-water';  // Bottle icon for milk
    case Allergens.TREE_NUTS:
      return 'fa-solid fa-tree';          // Tree icon for tree nuts
    case Allergens.CELERY:
      return 'fa-solid fa-carrot';        // Carrot icon for celery
    case Allergens.MUSTARD:
      return 'fa-solid fa-pepper-hot';    // Hot pepper icon for mustard
    case Allergens.SESAME:
      return 'fa-solid fa-circle-dot';    // Dot icon for sesame seeds
    case Allergens.SULPHITES:
      return 'fa-solid fa-flask';         // Flask icon for sulphites
    case Allergens.LUPIN:
      return 'fa-solid fa-seedling';      // Seedling icon for lupin
    case Allergens.MOLLUSCS:
      return 'fa-solid fa-water';         // Water icon for molluscs
    default:
      return 'fa-solid fa-circle-exclamation'; // Default warning icon
  }
} 

/**
 * Get the CSS classes for a specific food type tag
 * @param tag The food type tag
 * @returns The corresponding CSS classes for styling
 */
static getFoodTypeClasses(tag: Tags): string {
  switch (tag) {
    // Spice levels
    case Tags.SPICY:
      return 'bg-red-100 text-red-800';

    // Dietary preferences
    case Tags.VEGAN:
      return 'bg-green-100 text-green-800';
    case Tags.VEGETARIAN:
      return 'bg-emerald-100 text-emerald-800';
    case Tags.GLUTEN_FREE:
      return 'bg-violet-100 text-violet-800';
    case Tags.DAIRY_FREE:
      return 'bg-fuchsia-100 text-fuchsia-800';
    case Tags.HALAL:
      return 'bg-blue-100 text-blue-800';
    case Tags.KOSHER:
      return 'bg-indigo-100 text-indigo-800';
    case Tags.KETO:
      return 'bg-purple-100 text-purple-800';
    case Tags.PALEO:
      return 'bg-amber-100 text-amber-800';
    case Tags.SEAFOOD:
      return 'bg-cyan-100 text-cyan-800';

    // Special features
    case Tags.NEW:
      return 'bg-pink-100 text-pink-800';
    case Tags.CHEFS_CHOICE:
      return 'bg-yellow-100 text-yellow-800';
    case Tags.RECOMMENDED:
      return 'bg-teal-100 text-teal-800';
    case Tags.HOUSE_SPECIALTY:
      return 'bg-sky-100 text-sky-800';
    case Tags.LIMITED_EDITION:
      return 'bg-rose-100 text-rose-800';
    case Tags.SHARING:
      return 'bg-blue-100 text-blue-800';
    case Tags.BREAKFAST:
      return 'bg-amber-100 text-amber-800';

    // Quality indicators
    case Tags.ORGANIC:
      return 'bg-lime-100 text-lime-800';
    case Tags.LOCAL:
      return 'bg-amber-100 text-amber-800';
    case Tags.HOMEMADE:
      return 'bg-orange-100 text-orange-800';
    case Tags.NO_ADDITIVES:
      return 'bg-green-100 text-green-800';

    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Get the CSS classes for allergen tags
 * @returns The CSS classes for allergen styling
 */
static getAllergenClasses(): string {
  return 'bg-yellow-100 text-yellow-800';
}

/**
 * Get the color class for a specific food type tag
 * @param tag The food type tag
 * @returns The corresponding Tailwind CSS color class
 */
static getFoodTypeColorClass(tag: Tags): string {
  switch (tag) {
    // Spice levels
    case Tags.SPICY:
      return 'text-red-500';

    // Dietary preferences
    case Tags.VEGETARIAN:
      return 'text-green-500';
    case Tags.VEGAN:
      return 'text-emerald-500';
    case Tags.GLUTEN_FREE:
      return 'text-violet-500';
    case Tags.DAIRY_FREE:
      return 'text-fuchsia-500';
    case Tags.HALAL:
      return 'text-blue-500';
    case Tags.KOSHER:
      return 'text-indigo-500';
    case Tags.KETO:
      return 'text-purple-500';
    case Tags.PALEO:
      return 'text-amber-500';
    case Tags.SEAFOOD:
      return 'text-cyan-500';

    // Special features
    case Tags.NEW:
      return 'text-pink-500';
    case Tags.CHEFS_CHOICE:
      return 'text-yellow-600';
    case Tags.RECOMMENDED:
      return 'text-teal-500';
    case Tags.HOUSE_SPECIALTY:
      return 'text-sky-500';
    case Tags.LIMITED_EDITION:
      return 'text-rose-500';
    case Tags.SHARING:
      return 'text-blue-500';
    case Tags.BREAKFAST:
      return 'text-amber-500';

    // Quality indicators
    case Tags.ORGANIC:
      return 'text-lime-500';
    case Tags.LOCAL:
      return 'text-amber-500';
    case Tags.HOMEMADE:
      return 'text-orange-500';
    case Tags.NO_ADDITIVES:
      return 'text-green-500';

    default:
      return 'text-gray-500';
  }
}
}